/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { AccountInfo, ISource, SourceConfig } from '@stores/source';
import { isValidationLoadingType } from '@components/sources/source/addSource';
import { ISourceDataStore } from '@stores/sourceDataStore';
import { DIRECTORY_TABS } from '@components/common/constants';
import removeNullValues from '@utils/removeNullValues';
import { apiAuthCaller } from '@services/apiCaller';
import SelectResources from '../source/cloudSource/cloudSourceResources';
import CloudSourceSettings, { Resource } from '../source/cloudSource/cloudSourceSettings';
import { IDestination } from '@stores/destination';

export type DirectoryTab = typeof DIRECTORY_TABS[keyof typeof DIRECTORY_TABS];

export interface CredentialsAuth {
  handleChange: (
    accountId: string,
    accountInfo: AccountInfo | undefined,
    isValid?: boolean | undefined,
    submit?: boolean | undefined,
  ) => Promise<void>;
  source?: ISource;
  isValidationDisable: boolean;
  isValidationLoading: isValidationLoadingType;
  checkValid: (result?: isValidationLoadingType, isAccountDeleted?: boolean) => void;
  credentials: {
    accountId?: string;
    accountInfo?: { options?: Record<string, unknown>; secret?: Record<string, unknown> };
  };
}

export interface AccountValidator {
  source: ISourceDataStore | undefined;
  isNewAccountName: (name: string) => Promise<boolean>;
  isOauthSource: (name: string) => boolean;
  accountInfo: AccountInfo;
  setCredentials: (credentials: {
    accountId: string;
    accountInfo: unknown;
    valid: boolean;
    updated: boolean;
  }) => void;
  showErrorMessage: ((msg: string) => void) | undefined;
  accountId?: string;
  valid?: boolean;
}

export interface ResourcesComponentProps {
  source: ISourceDataStore;
  disabled?: boolean;
  onChange: (config: SourceConfig) => void;
  onSingleResourceOrLess: () => void;
}

export interface ConfigurationComponentProps {
  source: ISourceDataStore;
  disabled?: boolean;
  onChange: (config: SourceConfig, isValid: boolean) => void;
}

export abstract class SourceCategory {
  abstract getSourceType(sourceDef: { name: string }): string;

  abstract isNewConnectionAllowed(source: { destinations: unknown[] }): boolean;

  abstract getTabs(source: ISource): string[];

  abstract getAccountsEndpoint(sourceDef?: string): string;

  abstract handleSetup(
    canCreateCloud: boolean,
    canCreateWarehouse: boolean,
    create: () => void,
  ): boolean;

  abstract getAuthComponent(props: CredentialsAuth): JSX.Element;

  abstract validateAccount(props: AccountValidator): Promise<string | undefined>;

  abstract isPartOf(tab: DirectoryTab): boolean;

  hideFromAllowedDestConnections(): boolean {
    return false;
  }

  hasTransformationStep(_sourceId: string, isDeviceMode: boolean): boolean {
    return !isDeviceMode;
  }

  hasWHMappingStep(_selectedSource: unknown): boolean {
    return false;
  }

  getCredentialSettings(sourceDataStore: ISourceDataStore, isAdmin: boolean): JSX.Element | null {
    return null;
  }

  isWarehouse(): boolean {
    return false;
  }

  showTablePrefixSettings(): boolean {
    return false;
  }

  showSyncScheduleSettings(): boolean {
    return false;
  }

  enableCredentialValidations(): boolean {
    return false;
  }

  getSyncType(_type?: string): string {
    return 'FULL';
  }

  isReportsViewEnabled(): boolean {
    return false;
  }

  isSyncDelayEnabled(): boolean {
    return false;
  }

  isDestinationAllowedForConnection(destination: IDestination) {
    return true;
  }

  transformSourceConfig(config: SourceConfig): Record<string, unknown> {
    const {
      accountInfo,
      customResources,
      credentials,
      accessToken,
      refreshToken,
      rudderAccountId,
      ...updatedConfig
    } = config;
    delete updatedConfig?.role;
    return removeNullValues({
      ...updatedConfig,
    });
  }

  transformResourcesConfig(config: SourceConfig): Record<string, unknown> {
    const {
      accountInfo,
      customResources,
      credentials,
      accessToken,
      refreshToken,
      rudderAccountId,
      ...updatedConfig
    } = config;
    const newResources = (customResources || []).reduce((accum: string[], rsrc: { id: string }) => {
      if (!updatedConfig.resources!.includes(rsrc.id)) {
        accum.push(rsrc.id);
      }
      return accum;
    }, []);
    delete updatedConfig?.role;
    return removeNullValues({
      ...updatedConfig,
      resources: [...updatedConfig.resources!, ...newResources],
    });
  }

  getResourcesComponent({
    source,
    disabled,
    onChange,
    onSingleResourceOrLess,
  }: ResourcesComponentProps): JSX.Element {
    return (
      <SelectResources
        metadata={{
          params: {
            accountId: source?.config?.accountId || source?.config?.rudderAccountId,
          },
        }}
        // when editing resources will be in form of string only.
        selectedResources={source?.config?.resources?.map((elem: Resource) => {
          if (typeof elem === 'string') {
            return elem;
          }
          return elem.id;
        })}
        selectedColumns={source?.config?.resourcesColumns}
        customResources={source?.config?.customResources}
        onSingleResourceOrLess={onSingleResourceOrLess}
        sourceId={source?.id}
        sourceDefinition={source!.sourceDef}
        onChange={(resources, customResources, resourcesColumns) =>
          onChange({ resources, customResources, resourcesColumns })
        }
        disabled={disabled}
      />
    );
  }

  getConfigurationComponent(props: ConfigurationComponentProps): JSX.Element {
    const { source, onChange, disabled } = props;
    const { sourceDef, config } = source;
    return (
      <CloudSourceSettings
        metadata={{
          params: {
            accountId: config?.accountId || config?.rudderAccountId,
          },
        }}
        initialSettings={config}
        onSettingsChange={onChange}
        sourceDefinition={sourceDef}
        disabled={disabled}
      />
    );
  }

  hasIncompatibleSyncMode(
    customerSyncBehaviours?: string[],
    srcSyncBehaviours?: string[],
    destSyncBehaviours?: string[],
  ): boolean {
    return false;
  }

  async validateSource(
    token: string,
    config: SourceConfig,
    source: { id?: string; config?: SourceConfig; sourceDef: ISourceDefinition },
  ): Promise<{ valid: boolean; reason: string }> {
    if (!source.config) {
      throw new Error('Source config is Required');
    }
    const url = `/cloudSources/info/roles/${source.sourceDef.name}/source/validate`;
    const response = await apiAuthCaller(token, false, 30000).post(url, {
      ...source.config,
      ...config,
    });
    return response.data;
  }
}
